import * as React from "react"
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import type { Table } from "@tanstack/react-table"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../internal/primitives/select"
import { cn } from "../../../lib/utils"

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  // Fix #14: saner geometric defaults vs the old linear [10,20,30,40,50]
  pageSizeOptions?: number[]
  className?: string
  unknownTotal?: boolean
  /** Explicit total row count for "Showing X–Y of Z" display. In manual/server mode pass the
   *  server total; in client mode pass the post-filter count. Supplied by DataTable automatically. */
  rowCount?: number
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 25, 50, 100],
  className,
  unknownTotal = false,
  rowCount,
}: DataTablePaginationProps<TData>) {
  const pageIndex = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  const pageCount = table.getPageCount() || 1
  const selectedCount = table.getFilteredSelectedRowModel().rows.length

  // Fix #11: fully controlled draft so external page changes don't blow away in-flight edits
  const [draftPage, setDraftPage] = React.useState(String(pageIndex + 1))
  React.useEffect(() => {
    setDraftPage(String(pageIndex + 1))
  }, [pageIndex])

  const commitPageJump = () => {
    const page = Number(draftPage)
    if (!Number.isNaN(page) && page >= 1 && page <= pageCount) {
      table.setPageIndex(page - 1)
    } else {
      // Revert to current page if invalid
      setDraftPage(String(pageIndex + 1))
    }
  }

  // Fix #1: "Showing X–Y of Z" — derive start/end from actual visible row count
  // so the range is correct on the final (partial) page.
  const currentPageRowCount = table.getRowModel().rows.length
  const startRow = pageIndex * pageSize + 1
  const endRow = pageIndex * pageSize + currentPageRowCount
  const hasRows = currentPageRowCount > 0

  const pageSizeSelect = (
    <div className="flex items-center space-x-2">
      <p className="whitespace-nowrap text-[11px] font-medium text-muted-foreground">Rows per page</p>
      <Select
        value={`${pageSize}`}
        onValueChange={(value: string) => {
          table.setPageSize(Number(value))
        }}
      >
        <SelectTrigger className="h-7 w-[64px] border-border/40 bg-transparent text-[11px] font-bold hover:bg-muted/50">
          <SelectValue placeholder={pageSize} />
        </SelectTrigger>
        <SelectContent side="top">
          {pageSizeOptions.map((option) => (
            <SelectItem key={option} value={`${option}`} className="text-[11px] font-bold">
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  const navButtons = (
    <div className="flex items-center gap-1">
      {!unknownTotal ? (
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          <span className="sr-only">Go to first page</span>
          <ChevronsLeft className="h-3.5 w-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
      >
        <span className="sr-only">Go to previous page</span>
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
      >
        <span className="sr-only">Go to next page</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
      {!unknownTotal ? (
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
          onClick={() => table.setPageIndex(pageCount - 1)}
          disabled={!table.getCanNextPage()}
        >
          <span className="sr-only">Go to last page</span>
          <ChevronsRight className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )

  return (
    <div className={cn("px-1 py-1 sm:py-2", className)}>
      {/* Fix #4: Mobile layout now includes the page-size selector so narrow-viewport
          users aren't locked out of changing row density. */}
      <div className="flex flex-col gap-2 sm:hidden">
        <div className="flex items-center justify-between gap-2">
          {pageSizeSelect}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
              {unknownTotal
                ? `Page ${pageIndex + 1}`
                : `P. ${pageIndex + 1} / ${pageCount}`}
            </span>
            {navButtons}
          </div>
        </div>
        {/* Fix #1: Row range on mobile too */}
        {!unknownTotal && hasRows && (
          <span className="text-[10px] font-bold uppercase tracking-tight text-muted-foreground/60">
            {selectedCount > 0 ? `${selectedCount} sel. · ` : ""}
            {startRow}–{endRow}{rowCount != null ? ` of ${rowCount}` : ""}
          </span>
        )}
      </div>

      {/* Desktop: row count on the left, controls on the right */}
      <div className="hidden sm:flex items-center justify-between">
        {/* Fix #1: Persistent "Showing X–Y of Z" label replaces the conditional selection
            count that previously left the left side empty most of the time. */}
        <div className="flex-1 text-[11px] font-medium tabular-nums text-muted-foreground/80">
          {!unknownTotal && hasRows ? (
            <>
              {selectedCount > 0 ? (
                <span className="font-bold text-primary/80">{selectedCount} selected · </span>
              ) : null}
              Showing <span className="font-bold text-foreground/80">{startRow}–{endRow}</span>{rowCount != null ? <> of <span className="font-bold text-foreground/80">{rowCount}</span></> : ""}
            </>
          ) : selectedCount > 0 ? (
            <span className="font-bold text-primary/80">{selectedCount} rows selected</span>
          ) : null}
        </div>

        <div className="flex items-center space-x-4 lg:space-x-8">
          {pageSizeSelect}

          <div className="flex items-center gap-1.5">
            {unknownTotal ? (
              <span className="text-[11px] font-medium tabular-nums text-foreground/80">
                {`Page ${pageIndex + 1}`}
              </span>
            ) : (
              <>
                <span className="text-[11px] font-medium text-muted-foreground">Page</span>
                {/* Fix #11: Fully controlled input — no more key={pageIndex} remount trick.
                    Draft state tracks typing; committed value reverts on Escape or invalid blur. */}
                <input
                  type="number"
                  min={1}
                  max={pageCount}
                  value={draftPage}
                  aria-label="Go to page"
                  onChange={(e) => setDraftPage(e.target.value)}
                  onBlur={commitPageJump}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur()
                    if (e.key === "Escape") {
                      setDraftPage(String(pageIndex + 1))
                      e.currentTarget.blur()
                    }
                  }}
                  className="h-7 w-10 rounded-md border border-border/40 bg-transparent text-center text-[11px] font-bold tabular-nums appearance-none transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                  of {pageCount}
                </span>
              </>
            )}
          </div>

          {navButtons}
        </div>
      </div>
    </div>
  )
}
